// Serialises access to a single UCI engine.
//
// A UCI engine searches one position at a time and answers every `go` with exactly one `bestmove`,
// even after a `stop`. Sending `position`/`go` while a `bestmove` is still owed makes that stale
// `bestmove` look like the answer of the new search. The queue owns that invariant: one search in
// flight, every `bestmove` attributed to the search that produced it, and jobs ordered by priority so
// rating the move the user just played pre-empts the evaluation shown on screen.
//
// No DOM, no store: the transport is injected, which also makes it testable with a fake engine.

const PRIORITY = { rate: 3, prefetch: 2, display: 1 } as const;
const STUCK_AFTER_MS = 2500;

export type JobPriority = keyof typeof PRIORITY;

const NUMERIC_TOKENS = ["depth", "seldepth", "multipv", "nodes", "nps", "hashfull", "time"] as const;
type NumericToken = (typeof NUMERIC_TOKENS)[number];

/** One `info` line of a search. */
export interface UciInfo extends Partial<Record<NumericToken, number>> {
    multipv: number;
    bound: "lowerbound" | "upperbound" | null;
    pv?: string;
    scoreCp?: number;
    mate?: number;
}

/** Parses a UCI `info` line. Returns null for lines without a principal variation. */
export function parseInfoLine(text: string): UciInfo | null {
    if (!text.startsWith("info ")) return null;
    const parts = text.split(/\s+/);
    const info: UciInfo = { multipv: 1, bound: null };
    for (let i = 1; i < parts.length; i++) {
        const token = parts[i]!;
        if (token === "pv") {
            info.pv = parts.slice(i + 1).join(" ");
            break;
        }
        if (token === "score") {
            const value = Number(parts[i + 2]);
            if (parts[i + 1] === "cp") info.scoreCp = value;
            if (parts[i + 1] === "mate") info.mate = value;
            i += 2;
        } else if (token === "lowerbound" || token === "upperbound") {
            info.bound = token;
        } else if ((NUMERIC_TOKENS as readonly string[]).includes(token)) {
            info[token as NumericToken] = Number(parts[i + 1]);
            i++;
        }
    }
    return info.pv ? info : null;
}

export interface AnalyseRequest {
    fen: string;
    depth?: number;
    movetime?: number;
    searchmoves?: string[];
    multiPV?: number;
    priority?: JobPriority;
    /** Only the latest job of a group matters; starting one cancels the others. */
    group?: string | null;
    /** Identity of the search: asking again for the same key joins the job already in flight. */
    key?: string | null;
    /** A pre-empted job goes back in line instead of resolving. */
    resumable?: boolean;
    /**
     * UCI options this search needs, sent just before it starts. Playing at a limited strength is
     * one; setting them from outside would land them in the middle of whatever is running.
     */
    options?: string[];
    onInfo?: (info: UciInfo, lines: Record<number, UciInfo>) => void;
}

export interface AnalyseResult {
    cancelled?: boolean;
    reason?: string;
    fen?: string;
    bestmove?: string | null;
    lines?: UciInfo[];
    preempted?: boolean;
    depth?: number;
}

export interface AnalyseHandle {
    promise: Promise<AnalyseResult>;
    cancel: () => void;
}

interface Job {
    id: number;
    key: string | null;
    request: AnalyseRequest;
    priority: number;
    group: string | null;
    lines: Record<number, UciInfo>;
    cancelled: boolean;
    preempted: boolean;
    stopping?: boolean;
    resolve: (result: AnalyseResult) => void;
    handle: AnalyseHandle;
}

export interface Timers {
    set: (fn: () => void, ms: number) => unknown;
    clear: (id: unknown) => void;
}

export interface EngineQueue {
    analyse: (request: AnalyseRequest) => AnalyseHandle;
    handleLine: (text: string) => void;
    cancelAll: (reason?: string) => void;
    isIdle: () => boolean;
}

/**
 * @param send     writes one UCI command to the engine
 * @param onStuck  called when the engine does not answer a `stop`; the owner should recreate it
 * @param timers   replaceable in tests. The defaults call the global functions rather than hold
 *                 references to them: as a property of this object their receiver would be the object
 *                 and the browser refuses the call ("Illegal invocation").
 */
export function createEngineQueue({
    send,
    onStuck = () => {},
    timers = { set: (fn: () => void, ms: number) => setTimeout(fn, ms), clear: (id: unknown) => clearTimeout(id as number) },
}: {
    send: (command: string) => void;
    onStuck?: () => void;
    timers?: Timers;
}): EngineQueue {
    let active: Job | null = null;
    let pending: Job[] = [];
    let multiPv: number | null = null;
    let stuckTimer: unknown = null;
    let sequence = 0;

    function analyse(request: AnalyseRequest): AnalyseHandle {
        const key = request.key || null;
        const existing = key && [active, ...pending].find((job) => job && job.key === key && !job.cancelled);
        if (existing) return existing.handle;

        let resolve!: (result: AnalyseResult) => void;
        const promise = new Promise<AnalyseResult>((r) => { resolve = r; });
        const job: Job = {
            id: ++sequence, key, request, priority: PRIORITY[request.priority || "display"],
            group: request.group || null, lines: {}, cancelled: false, preempted: false,
            resolve,
            handle: { promise, cancel: () => cancel(job) },
        };

        // Only the latest job of a group matters (e.g. the on-screen evaluation of the current position).
        if (job.group) {
            [...pending].filter((other) => other.group === job.group).forEach(cancel);
            if (active && active.group === job.group) cancel(active);
        }

        pending.push(job);
        pending.sort((a, b) => b.priority - a.priority || a.id - b.id);
        if (active && !active.stopping && job.priority > active.priority) {
            active.preempted = true;
            stop(active);
        }
        pump();
        return job.handle;
    }

    function cancel(job: Job): void {
        if (job.cancelled) return;
        job.cancelled = true;
        if (job === active) {
            stop(job);
            return;
        }
        pending = pending.filter((other) => other !== job);
        job.resolve({ cancelled: true });
    }

    function stop(job: Job): void {
        if (job.stopping) return;
        job.stopping = true;
        send("stop");
        stuckTimer = timers.set(() => {
            stuckTimer = null;
            cancelAll("engine did not answer stop");
            onStuck();
        }, STUCK_AFTER_MS);
    }

    function pump(): void {
        if (active || !pending.length) return;
        active = pending.shift()!;
        const { fen, depth, movetime, searchmoves, options } = active.request;
        const wantedMultiPv = active.request.multiPV || 1;
        if (wantedMultiPv !== multiPv) {
            multiPv = wantedMultiPv;
            send(`setoption name MultiPV value ${multiPv}`);
        }
        // Whatever this search needs set, set between the last one and this one
        (options || []).forEach((option) => send(option));
        send(`position fen ${fen}`);
        const limits = [depth ? `depth ${depth}` : "", movetime ? `movetime ${movetime}` : ""].filter(Boolean).join(" ");
        send(`go ${limits}${searchmoves && searchmoves.length ? ` searchmoves ${searchmoves.join(" ")}` : ""}`);
    }

    function handleLine(text: string): void {
        if (!active) return;   // a bestmove nobody is waiting for
        if (text.startsWith("bestmove")) {
            finish(text.split(/\s+/)[1] || null);
            return;
        }
        const info = parseInfoLine(text);
        // A bound is not a score: the search was cut before the real value was known.
        if (!info || info.bound || (info.scoreCp === undefined && info.mate === undefined)) return;
        active.lines[info.multipv] = info;
        if (active.request.onInfo && !active.cancelled) active.request.onInfo(info, { ...active.lines });
    }

    function finish(bestmove: string | null): void {
        const job = active;
        active = null;
        if (!job) return;
        if (stuckTimer) {
            timers.clear(stuckTimer);
            stuckTimer = null;
        }
        if (job.cancelled) {
            job.resolve({ cancelled: true });
        } else if (job.preempted && job.request.resumable) {
            // Goes back in line; the engine's hash table makes the restart cheap.
            job.preempted = false;
            job.stopping = false;
            job.lines = {};
            pending.push(job);
            pending.sort((a, b) => b.priority - a.priority || a.id - b.id);
        } else {
            const lines = Object.keys(job.lines).map(Number).sort((a, b) => a - b).map((n) => job.lines[n]!);
            job.resolve({
                fen: job.request.fen, bestmove, lines, preempted: job.preempted,
                depth: lines.length ? Math.min(...lines.map((l) => l.depth || 0)) : 0,
            });
        }
        pump();
    }

    function cancelAll(reason = "cancelled"): void {
        const jobs = [active, ...pending].filter((job): job is Job => !!job);
        active = null;
        pending = [];
        multiPv = null;
        if (stuckTimer) {
            timers.clear(stuckTimer);
            stuckTimer = null;
        }
        jobs.forEach((job) => job.resolve({ cancelled: true, reason }));
    }

    return { analyse, handleLine, cancelAll, isIdle: () => !active && !pending.length };
}
